import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import api from '../../lib/api';
import {
  CreateOrganizationPayload,
  RecentEditedLoad,
  UpdateOrganizationPayload,
  UpdateOrganizationAddressPayload,
} from './types';

export * from './types';

export const qk = {
  org: ['organization'] as const,
};

export function useGetOrganization(enabled = true) {
  return useQuery({
    queryKey: qk.org,
    queryFn: async () => {
      const resp = await api.get('/organization');
      return resp.data?.organization ?? resp.data;
    },
    enabled,
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateOrganizationPayload) => {
      const resp = await api.post('/organization', payload);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.org });
      qc.invalidateQueries({ queryKey: ['users', 'me'] });
    },
  });
}

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateOrganizationPayload) => {
      const resp = await api.put('/organization', payload);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.org });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useUpdateOrganizationAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateOrganizationAddressPayload) => {
      const resp = await api.put('/organization/address', payload);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.org });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useArchiveOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const resp = await api.post('/organization/archive');
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.org });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useUnarchiveOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const resp = await api.post('/organization/unarchive');
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.org });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useUploadOrgAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/organization/agreement', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.org });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useDeleteOrgAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.delete('/organization/agreement')).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.org });
      qc.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export function useDownloadOrgAgreement() {
  return useMutation({
    mutationFn: async () => {
      const resp = await api.get('/organization/agreement/download', { responseType: 'blob' });
      return resp.data as Blob;
    },
  });
}

export function useListAuditLogs(params: {
  entityType?: string;
  entityId?: string;
  page?: number;
  pageSize?: number;
}) {
  const normalized = {
    entityType: params.entityType ?? '',
    entityId: params.entityId ?? '',
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 25,
  };

  return useQuery({
    queryKey: [
      'auditLogs',
      normalized.entityType,
      normalized.entityId,
      normalized.page,
      normalized.pageSize,
    ],
    queryFn: async () => {
      const resp = await api.get('/organization/audit-logs', { params });
      return resp.data;
    },
    enabled: !!params.entityType,
    placeholderData: keepPreviousData,
  });
}

/** The loads most recently edited, newest first. Audit-driven — see the route. */
export function useRecentEditedLoads(params: { limit?: number; enabled?: boolean }) {
  const limit = params.limit ?? 20;

  return useQuery<{ loads: RecentEditedLoad[] }>({
    queryKey: ['auditLogs', 'recentLoads', limit],
    queryFn: async () => {
      const resp = await api.get('/organization/audit-logs/recent-loads', { params: { limit } });
      return resp.data;
    },
    enabled: params.enabled !== false,
    placeholderData: keepPreviousData,
  });
}

/**
 * The loads the current user most recently edited, newest first. Same route as
 * useRecentEditedLoads but scoped to my own audit trail (mine=true), so the
 * Orders sidebar flyout never shows another admin's edits.
 */
export function useMyRecentEditedLoads(params: { limit?: number; enabled?: boolean }) {
  const limit = params.limit ?? 5;

  return useQuery<{ loads: RecentEditedLoad[] }>({
    queryKey: ['auditLogs', 'recentLoads', 'mine', limit],
    queryFn: async () => {
      const resp = await api.get('/organization/audit-logs/recent-loads', {
        params: { limit, mine: true },
      });
      return resp.data;
    },
    enabled: params.enabled !== false,
    placeholderData: keepPreviousData,
  });
}

export function useAuditLogFilterOptions(params: {
  entityType?: string;
  q?: string;
  limit?: number;
}) {
  const normalized = {
    entityType: params.entityType ?? '',
    q: params.q ?? '',
    limit: params.limit ?? 20,
  };

  return useQuery({
    queryKey: ['auditLogFilterOptions', normalized.entityType, normalized.q, normalized.limit],
    queryFn: async () => {
      const resp = await api.get('/organization/audit-logs/filter-options', {
        params: normalized,
      });
      return resp.data;
    },
    enabled:
      !!normalized.entityType &&
      ['CUSTOMER', 'ORDER', 'PRODUCT', 'CARRIER'].includes(normalized.entityType),
  });
}
